/**
 * The `DEVICE_REVOKED` screen.
 *
 * By the time this renders, local state is already wiped — the store's session handler does it
 * the moment the base query sees the code. This screen exists to explain, because being silently
 * signed out mid-shift looks like the app broke and ends with a phone call to the owner.
 */

import { useNavigate } from 'react-router';

import { selectSession } from '@pos/shared';

import { useAppSelector } from '@/app/hooks';
import { Button } from '@/shared/ui';

export function DeviceRevokedPage() {
  const navigate = useNavigate();
  const { revokedReason } = useAppSelector(selectSession);

  return (
    <main className="grid min-h-dvh place-items-center bg-surface-backdrop px-5">
      <div className="w-full max-w-column rounded-sheet bg-surface-base p-[1.75rem] text-center shadow-column">
        <div className="mx-auto mb-5 grid h-[3.5rem] w-[3.5rem] shrink-0 place-items-center rounded-full bg-warning-subtle text-warning-subtle-text">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 8v5m0 3.5h.01M10.3 3.9 2.5 17.5A1.8 1.8 0 0 0 4 20h16a1.8 1.8 0 0 0 1.5-2.5L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-content-primary">
          This device was signed out
        </h1>

        <p className="mt-3 text-base leading-relaxed text-content-secondary">
          {revokedReason ??
            'Someone signed in to this account on another device, so this one was signed out. Sign in with your password to take it back.'}
        </p>

        <p className="mt-4 text-sm text-content-muted">
          Everything stored on this device has been cleared, including the offline catalogue. It
          downloads again on the next sign-in.
        </p>

        <Button
          size="lg"
          fullWidth
          className="mt-[1.75rem]"
          onClick={() => void navigate('/sign-in', { replace: true })}
        >
          Sign in with password
        </Button>
      </div>
    </main>
  );
}
